import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Check, Crown, Shield, Zap, BookOpen, Award, Star, Clock, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { updateVipStatus } from '../utils/profilesAPI';

/**
 * VIP会员开通页面
 * 展示会员特权和不同套餐选项
 * @returns {JSX.Element} VIP会员页面组件
 */
function VIP() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [showFAQ, setShowFAQ] = useState({});
  const navigate = useNavigate();

  // 获取用户信息
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const storedUserInfo = localStorage.getItem('user_info');
        if (storedUserInfo) {
          setUserProfile(JSON.parse(storedUserInfo));
        } else {
          // 未登录，跳转到登录页
          navigate('/login', { state: { message: '请先登录' } });
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  // VIP会员特权列表
  const vipBenefits = [
    {
      title: '全部题库解锁',
      description: '解锁所有题库和模拟试卷',
      icon: BookOpen,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: '专属学习路径',
      description: '个性化学习计划与推荐',
      icon: Zap,
      color: 'bg-amber-100 text-amber-600'
    },
    {
      title: '考试重点标记',
      description: '智能标记考试重点和难点',
      icon: Star,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: '无限次考试模拟',
      description: '不限次数模拟考试体验',
      icon: Clock,
      color: 'bg-green-100 text-green-600'
    },
    {
      title: '专属会员标识',
      description: '显示VIP专属身份标识',
      icon: Crown,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      title: '学习数据分析',
      description: '详细的学习进度和成绩分析',
      icon: Award,
      color: 'bg-red-100 text-red-600'
    }
  ];

  // 会员套餐
  const vipPlans = {
    monthly: {
      name: '月度会员',
      price: 28,
      originalPrice: 39,
      duration: '1个月',
      popular: false,
      discount: '28% OFF'
    },
    quarterly: {
      name: '季度会员',
      price: 68,
      originalPrice: 99,
      duration: '3个月',
      popular: true,
      discount: '31% OFF'
    },
    yearly: {
      name: '年度会员',
      price: 198,
      originalPrice: 399,
      duration: '12个月',
      popular: false,
      discount: '50% OFF'
    }
  };

  // 常见问题列表
  const faqs = [
    {
      question: 'VIP会员有哪些特权？',
      answer: 'VIP会员可以解锁全部题库、获取专属学习路径、使用考试重点标记功能、无限次模拟考试、获得专属会员标识以及详细的学习数据分析服务。'
    },
    {
      question: '如何开通VIP会员？',
      answer: '选择您心仪的会员套餐，点击"立即开通"按钮，根据系统提示完成支付即可成功开通VIP会员。'
    },
    {
      question: '会员到期后会自动续费吗？',
      answer: '不会。我们不支持自动续费，会员到期后需要您手动续费。我们会在会员到期前向您发送提醒通知。'
    },
    {
      question: '可以退款吗？',
      answer: '开通会员后7天内未使用高级功能可申请全额退款，超过7天或已使用高级功能将无法退款。请联系客服申请退款。'
    },
    {
      question: '不同设备可以同时使用一个账号吗？',
      answer: '可以。您可以在多台设备上使用同一个账号，但同一时间内只能在一台设备上活跃使用。'
    }
  ];

  // 处理开通VIP
  const handleSubscribe = async () => {
    if (!userProfile) {
      navigate('/login', { state: { message: '请先登录' } });
      return;
    }

    try {
      setLoading(true);

      // 这里应该连接到实际的支付接口
      // 模拟支付成功
      setTimeout(async () => {
        try {
          // 计算VIP过期时间（30天后）
          const vipExpireDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
          
          // 使用profilesAPI中的updateVipStatus函数更新用户VIP状态
          const result = await updateVipStatus(userProfile._id, true, vipExpireDate);

          if (!result.success) throw new Error(result.error || '更新VIP状态失败');

          // 更新本地存储的用户信息
          const updatedUserInfo = {
            ...userProfile,
            isVip: true,
            vipExpireDate
          };
          localStorage.setItem('user_info', JSON.stringify(updatedUserInfo));
          setUserProfile(updatedUserInfo);
          setSuccess(true);

          // 3秒后返回首页
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } catch (error) {
          console.error('更新VIP状态失败:', error);
          alert('开通失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      }, 2000);
    } catch (error) {
      console.error('支付失败:', error);
      setLoading(false);
      alert('支付失败，请稍后重试');
    }
  };

  // 切换FAQ展开状态
  const toggleFAQ = (index) => {
    setShowFAQ(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="flex flex-col min-h-screen app-bg-color">
      <Header title="VIP会员" />
      
      <div className="flex-1 pb-24">
        {/* 顶部横幅 */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-300 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <Crown className="w-6 h-6 mr-2" />
                VIP会员专享特权
              </h1>
              <p className="text-sm mt-2 text-white/90">
                解锁全部功能，提升学习效率
              </p>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        {/* 会员特权 */}
        <div className="px-4 py-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">会员特权</h2>
          <div className="grid grid-cols-2 gap-4">
            {vipBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                <div className={`w-10 h-10 ${benefit.color} rounded-full flex items-center justify-center mb-2`}>
                  <benefit.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-800">{benefit.title}</h3>
                <p className="text-xs text-gray-500 mt-1">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 会员套餐 */}
        <div className="px-4 pb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">选择套餐</h2>
          <div className="space-y-4">
            {Object.keys(vipPlans).map((planKey) => {
              const plan = vipPlans[planKey];
              return (
                <div 
                  key={planKey}
                  className={`relative border-2 rounded-lg p-4 ${
                    selectedPlan === planKey 
                      ? 'border-yellow-500 bg-yellow-50' 
                      : 'border-gray-200 bg-white'
                  }`}
                  onClick={() => setSelectedPlan(planKey)}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 -right-1 bg-red-500 text-white text-xs px-3 py-1 rounded-full">
                      最受欢迎
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-gray-800">{plan.name}</h3>
                      <div className="flex items-baseline mt-1">
                        <span className="text-2xl font-bold text-gray-800">¥{plan.price}</span>
                        <span className="text-xs text-gray-500 line-through ml-2">¥{plan.originalPrice}</span>
                        <span className="text-xs text-red-500 ml-2">{plan.discount}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">有效期：{plan.duration}</p>
                    </div>
                    <div 
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedPlan === planKey 
                          ? 'bg-yellow-500 border-yellow-500' 
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {selectedPlan === planKey && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 常见问题 */}
        <div className="px-4 pb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">常见问题</h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg overflow-hidden shadow-sm"
              >
                <div 
                  className="flex justify-between items-center p-4 cursor-pointer"
                  onClick={() => toggleFAQ(index)}
                >
                  <h3 className="font-medium text-gray-800">{faq.question}</h3>
                  {showFAQ[index] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                {showFAQ[index] && (
                  <div className="p-4 pt-0 border-t border-gray-100">
                    <p className="text-sm text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 提示信息 */}
        <div className="px-4 pb-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex">
            <AlertCircle className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              开通VIP会员即表示您同意《会员服务协议》。如有问题，请联系客服。
            </p>
          </div>
        </div>
      </div>

      {/* 底部开通按钮 */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
        {success ? (
          <div className="bg-green-500 text-white p-4 rounded-lg text-center">
            <Check className="w-6 h-6 mx-auto mb-2" />
            <p className="font-semibold">恭喜您！VIP会员开通成功</p>
            <p className="text-sm mt-1">3秒后自动返回首页...</p>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-bold text-gray-800">¥{vipPlans[selectedPlan].price}</span>
              <span className="text-xs text-gray-500 ml-2 line-through">¥{vipPlans[selectedPlan].originalPrice}</span>
            </div>
            <button
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-white font-semibold px-6 py-3 rounded-lg shadow-md flex items-center disabled:opacity-70"
              onClick={handleSubscribe}
              disabled={loading}
            >
              {loading ? '处理中...' : '立即开通'}
              {!loading && <Crown className="w-5 h-5 ml-2" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default VIP; 